import React, {useEffect, useState} from 'react';

import {fetch} from 'molbio/dao/FeatureLibraryDAO';
import {FeatureLibrary} from 'molbio/feature-libraries/FeatureLibraryConstants';
import {formatFeatureLibraryName} from 'molbio/feature-libraries/featureLibraryUtil';
import notifications from 'notifications';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {AsyncStatus, useAsyncStateOnMount} from 'ui-library/hooks/useAsyncState';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import Spinner from 'ui-library/Spinner';

export default function AddAnnotationToFeatureLibraryModal({
  annotationResidues,
  onRequestSubmit,
  onRequestClose,
}: {
  annotationResidues: string;
  onRequestSubmit: (featureLibraryId: number) => void;
  onRequestClose: () => void;
}): JSX.Element {
  const featureLibrariesState = useAsyncStateOnMount(fetch);
  const [selectedFeatureLibrary, setSelectedFeatureLibrary] = useState<FeatureLibrary>();
  useEffect(() => {
    if (featureLibrariesState.status === AsyncStatus.ERROR) {
      notifications.error('Encountered error loading feature libraries.');
      onRequestClose();
    } else if (featureLibrariesState.status === AsyncStatus.SUCCESS) {
      const writableFeatureLibraries = featureLibrariesState.data.filter(
        (library) => library.permissions.writable
      );
      if (writableFeatureLibraries.length > 0) {
        setSelectedFeatureLibrary(writableFeatureLibraries[0]);
      }
    }
  }, [featureLibrariesState, onRequestClose]);

  const modalProps = {
    title: 'Add Annotation to Feature Library',
    buttons: [
      {text: 'Add to library', submit: true, disabled: selectedFeatureLibrary == null},
      {text: 'Cancel', cancel: true},
    ],
    onRequestClose,
    onRequestSubmit: () => {
      onRequestSubmit(selectedFeatureLibrary!.id);
    },
  };

  if (featureLibrariesState.status === AsyncStatus.SUCCESS) {
    const selectedFeatureLibraryDisplayName =
      selectedFeatureLibrary == null
        ? 'No feature library selected'
        : formatFeatureLibraryName(selectedFeatureLibrary);
    return (
      <Modal {...modalProps}>
        <div>
          <div className="form-group">
            <SelectDropdown
              displayName={selectedFeatureLibraryDisplayName}
              menu={
                <SearchableMenu
                  optionDatas={featureLibrariesState.data.map((library) => ({
                    name: formatFeatureLibraryName(library),
                    value: library,
                    metadata: library.description,
                    disabled: !library.permissions.writable,
                    disabledMessage:
                      'You do not have sufficient permissions to write to this feature library.',
                  }))}
                  onSelect={setSelectedFeatureLibrary}
                  selectedValue={selectedFeatureLibrary}
                  width={MenuWidth.FULL}
                />
              }
            />
          </div>
          <div className="well well-sm">
            <p style={{wordBreak: 'break-all'}} className="mono">
              {annotationResidues}
            </p>
          </div>
        </div>
      </Modal>
    );
  } else {
    return (
      <Modal {...modalProps}>
        <Spinner />
      </Modal>
    );
  }
}
